const DEFAULT_MAX_CHARS = 360;
const DEFAULT_MAX_SENTENCES = 2;
const SPEECH_ECHO_WINDOW_MS = 15000;

let lastVoiceMaxxSpeech = {
  text: "",
  startedAt: 0,
  endedAt: 0,
};
let activeUtterance = null;
let pendingSpeakTimer = null;
let pendingVoicesCleanup = null;

function setSpeechRuntimeState(status, detail = {}) {
  if (typeof window === "undefined") return;
  window.__voiceMaxxSpeechState = {
    status,
    updatedAt: Date.now(),
    ...detail,
  };
  window.__voiceMaxxSpeaking = status === "queued" || status === "speaking";
}

function clearPendingSpeakTimer() {
  if (pendingSpeakTimer !== null) {
    const clear = typeof window !== "undefined" ? window.clearTimeout : clearTimeout;
    clear(pendingSpeakTimer);
    pendingSpeakTimer = null;
  }
  pendingVoicesCleanup?.();
  pendingVoicesCleanup = null;
}

function normalizedSpeech(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function cleanMarkdownForSpeech(value) {
  return String(value || "")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/https?:\/\/\S+/gi, " ")
    .replace(/\b[a-f0-9]{64}\b/gi, "verified evidence")
    .replace(/(?:^|\s)[#>*_-]+\s*/g, " ")
    .replace(/[*_~]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function boundedAtWord(value, maxChars) {
  const text = String(value || "").trim();
  if (text.length <= maxChars) return text;
  const bounded = text.slice(0, maxChars + 1);
  const lastSpace = bounded.lastIndexOf(" ");
  const result = (lastSpace > Math.floor(maxChars * 0.65)
    ? bounded.slice(0, lastSpace)
    : bounded.slice(0, maxChars)).replace(/[,:;\s]+$/, "");
  return `${result}.`;
}

export function buildSpokenVoiceReply(
  value,
  { maxChars = DEFAULT_MAX_CHARS, maxSentences = DEFAULT_MAX_SENTENCES } = {}
) {
  const text = cleanMarkdownForSpeech(value);
  if (!text) return "";
  const sentences = text
    .split(/(?<=[.!?])\s+/)
    .map((sentence) => sentence.trim())
    .filter(Boolean);
  const selected = [];
  for (const sentence of sentences) {
    if (selected.length >= maxSentences) break;
    const candidate = [...selected, sentence].join(" ");
    if (candidate.length > maxChars && selected.length) break;
    selected.push(sentence);
  }
  return boundedAtWord(selected.join(" ") || text, maxChars);
}

export function stopVoiceMaxxSpeech(synthesis = null) {
  const runtime = synthesis || (
    typeof window !== "undefined" ? window.speechSynthesis : null
  );
  if (!runtime || typeof runtime.cancel !== "function") return false;
  clearPendingSpeakTimer();
  runtime.cancel();
  activeUtterance = null;
  if (lastVoiceMaxxSpeech.startedAt && !lastVoiceMaxxSpeech.endedAt) {
    lastVoiceMaxxSpeech = {
      ...lastVoiceMaxxSpeech,
      endedAt: Date.now(),
    };
  }
  setSpeechRuntimeState("idle");
  return true;
}

export function isLikelyVoiceMaxxEcho(value, now = Date.now()) {
  const transcript = normalizedSpeech(value);
  const spoken = normalizedSpeech(lastVoiceMaxxSpeech.text);
  if (!transcript || !spoken) return false;
  if (/^(?:stop|stop speaking|cancel|pause|mute|be quiet|listen)$/i.test(transcript)) {
    return false;
  }
  const activeAt = lastVoiceMaxxSpeech.endedAt || lastVoiceMaxxSpeech.startedAt;
  if (!activeAt || now - activeAt > SPEECH_ECHO_WINDOW_MS) return false;
  return (
    spoken.includes(transcript) ||
    transcript.includes(spoken) ||
    (transcript.length >= 2 && spoken.startsWith(transcript))
  );
}

export function resetVoiceMaxxSpeechState() {
  clearPendingSpeakTimer();
  lastVoiceMaxxSpeech = { text: "", startedAt: 0, endedAt: 0 };
  activeUtterance = null;
  setSpeechRuntimeState("idle");
}

export function speakVoiceMaxx(
  value,
  {
    synthesis = typeof window !== "undefined" ? window.speechSynthesis : null,
    Utterance = typeof window !== "undefined" ? window.SpeechSynthesisUtterance : null,
    onStart,
    onEnd,
    onError,
  } = {}
) {
  const text = buildSpokenVoiceReply(value);
  if (!text || !synthesis || typeof synthesis.speak !== "function" || !Utterance) {
    setSpeechRuntimeState("unavailable", { text });
    return { spoken: false, text };
  }

  const replacesActiveSpeech = Boolean(
    activeUtterance || synthesis.speaking || synthesis.pending
  );
  if (replacesActiveSpeech) {
    stopVoiceMaxxSpeech(synthesis);
  }

  const utterance = new Utterance(text);
  activeUtterance = utterance;
  lastVoiceMaxxSpeech = {
    text,
    startedAt: Date.now(),
    endedAt: 0,
  };
  utterance.rate = 1;
  utterance.pitch = 1;
  const initialVoices = typeof synthesis.getVoices === "function"
    ? synthesis.getVoices()
    : [];
  const selectEnglishVoice = (voices) => {
    const englishVoice = voices.find((voice) => voice.localService===true&&/^en(?:-|_)/i.test(voice.lang || ""));
    if (englishVoice) utterance.voice = englishVoice;
  };
  selectEnglishVoice(initialVoices);
  utterance.onstart = () => {
    setSpeechRuntimeState("speaking", { text });
    onStart?.();
  };
  utterance.onend = () => {
    activeUtterance = null;
    lastVoiceMaxxSpeech = { ...lastVoiceMaxxSpeech, endedAt: Date.now() };
    setSpeechRuntimeState("idle", { text });
    onEnd?.();
  };
  utterance.onerror = (error) => {
    activeUtterance = null;
    lastVoiceMaxxSpeech = { ...lastVoiceMaxxSpeech, endedAt: Date.now() };
    setSpeechRuntimeState("error", {
      text,
      error: String(error?.error || error?.message || "speech_error"),
    });
    onError?.(error);
  };

  const enqueue = () => {
    pendingSpeakTimer = null;
    try {
      if(utterance.voice?.localService!==true){utterance.onerror?.({error:'local_voice_unavailable'});return;}
      if (synthesis.paused && typeof synthesis.resume === "function") {
        synthesis.resume();
      }
      synthesis.speak(utterance);
    } catch (error) {
      utterance.onerror?.(error);
    }
  };

  setSpeechRuntimeState("queued", { text });
  const enqueueReadyUtterance = () => {
    if (replacesActiveSpeech) {
      const schedule = typeof window !== "undefined" ? window.setTimeout : setTimeout;
      pendingSpeakTimer = schedule(enqueue, 0);
    } else {
      enqueue();
    }
  };

  if (
    initialVoices.length === 0 &&
    typeof synthesis.addEventListener === "function" &&
    typeof synthesis.removeEventListener === "function"
  ) {
    const schedule = typeof window !== "undefined" ? window.setTimeout : setTimeout;
    let settled = false;
    const handleVoicesReady = () => {
      if (settled) return;
      const voices = synthesis.getVoices?.() || [];
      if (!voices.length) return;
      settled = true;
      pendingVoicesCleanup?.();
      pendingVoicesCleanup = null;
      selectEnglishVoice(voices);
      enqueueReadyUtterance();
    };
    const fallbackTimer = schedule(() => {
      if (settled) return;
      settled = true;
      pendingVoicesCleanup?.();
      pendingVoicesCleanup = null;
      enqueueReadyUtterance();
    }, 600);
    synthesis.addEventListener("voiceschanged", handleVoicesReady);
    pendingVoicesCleanup = () => {
      synthesis.removeEventListener("voiceschanged", handleVoicesReady);
      const clear = typeof window !== "undefined" ? window.clearTimeout : clearTimeout;
      clear(fallbackTimer);
    };
  } else {
    enqueueReadyUtterance();
  }
  return { spoken: true, text, utterance, queued: true };
}
